class ProductRecommendations extends HTMLElement {
  #PNG_BADGE_BY_KEY = {
    premium: { text: 'Premium', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/premium.webp?v=1776444219'},
    aftermarket: { text: 'Aftermarket', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/after-market.webp?v=1776444218'},
    'after-market': { text: 'Aftermarket', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/after-market.webp?v=1776444218'},
    'premium-a': { text: 'Premium A', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/premium-a.png?v=1773913436'},
    'premium-b': { text: 'Premium B', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/premium-b.png?v=1773913435'},
    diagnostics: { text: 'Diagnostics', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/diagnostic.webp?v=1776444219'},
    diagonostic: { text: 'Diagnostics', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/diagnostic.webp?v=1776444219'},
    refurbished: { text: 'Refurbished', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/refurb.webp?v=1776444218'},
    'soft-oled': { text: 'Soft OLED', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/am-soft-oled-2.webp?v=1776444218'},
    incell: { text: 'Incell', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/am-incell-2.webp?v=1776446039'},
    'pulled-a': { text: 'Pulled A', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/pull-a.webp?v=1776444218'},
    'pulled-b': { text: 'Pulled B', img: 'https://cdn.shopify.com/s/files/1/0952/7391/0569/files/pull-b.webp?v=1776444218'},
  };

  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
      continue;
      this.#loadRecommendations();
      break;
    }
  });
  #cachedRecommendations = {};
  #activeFetch = null;
  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    // Server-rendered recommendations skip #loadRecommendations; still align PNG src with the canonical map.
    if (this.dataset.recommendationsPerformed === 'true') {
      requestAnimationFrame(() => this.#applyPngTypeBadges());
    }
  }
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;
    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }
    if (recommendationsPerformed === 'true') {
      return;
    }
    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#applyPngTypeBadges();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  #applyPngTypeBadges() {
    // Product recommendations are injected via AJAX; if the server-rendered HTML didn't
    // include PNG badges, this ensures they display correctly using the toggle state.
    const usePngTypeBadgesAttr = this.getAttribute('data-use-png-type-badges');
    if (usePngTypeBadgesAttr !== 'true') return;

    // Some recommendation cards might not use the same badge container classes,
    // so we only rely on the badge type class itself.
    const spans = this.querySelectorAll('.badges-item__type');
    spans.forEach((span) => {
      const typeClass = Array.from(span.classList).find((c) => c.startsWith('badges-item__type--'));
      if (!typeClass) return;
      const key = typeClass.replace('badges-item__type--', '');
      const conf = this.#PNG_BADGE_BY_KEY[key];
      if (!conf?.img) return;

      span.classList.add('badges-item__type--image');
      span.innerHTML = `
        <img class="badges-item__type__img"
             src="${conf.img}"
             alt="${conf.text}"
             loading="lazy"
             width="220"
             height="32">`;
    });
  }
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
